/**
 * News Panel — 3-tab slide-in panel: News, Events, Jobs.
 * Mirrors the Resources panel pattern (glassmorphism, close on overlay/Escape).
 */
import { closeSidebar } from './sidebar';
import { closeResourcesPanel } from './resources-panel';

// ── Client-side news filter ─────────────────────────────────────────────────
const NEWS_DISQUALIFY = [
  'drone strike', 'air strike', 'airstrike', 'missile strike', 'military strike',
  'drone attack', 'bomb', 'bombing', 'missile', 'warhead',
  'kill', 'killed', 'killing', 'casualties', 'casualty',
  'wounded', 'shot down', 'downed', 'assassination', 'targeted killing',
  'death toll', 'civilian deaths',
  'drone war', 'drone warfare', 'warzone', 'war zone', 'battlefield',
  'frontline', 'front line', 'combat',
  'munition', 'loitering munition', 'kamikaze',
  'insurgent', 'insurgency', 'rebel forces', 'militia', 'terrorist',
  'troops', 'soldiers deployed', 'battalion', 'regiment',
  'military', 'pentagon', 'nato',
  'defense contractor', 'defence contractor',
  'armed drone', 'combat drone', 'killer drone', 'suicide drone',
  'weaponized', 'weaponised',
  'machine gun', 'weapon system', 'weapons system',
  'shahed', 'bayraktar', 'switchblade', 'reaper drone',
  'predator drone', 'global hawk', 'heron drone', 'lancet',
  'espionage', 'spy drone', 'surveillance drone',
  'counter-drone', 'counter drone', 'anti-drone',
];
function isNewsDisqualified(title: string, description: string): boolean {
  const text = `${title} ${description}`.toLowerCase();
  return NEWS_DISQUALIFY.some(phrase => text.includes(phrase));
}

// ── Types ─────────────────────────────────────────────────────────────────────
type NPTab = 'news' | 'events' | 'jobs' | 'opportunities';

interface NewsArticle {
  id: string;
  title: string;
  url: string;
  excerpt: string;
  source: string;
  sourceLabel: string;
  sourceColor: string;
  pub_date: string;
  chipId: string;
}

interface EventItem {
  id: string;
  title: string;
  date: string;
  url: string;
  description: string;
  location: string;
  type: string;
}

interface JobItem {
  id: string;
  company: string;
  title: string;
  location: string;
  url: string;
  summary: string;
  pub_date: string;
  source: string;
}

export interface OpportunityItem {
  id: string;
  title: string;
  issuer: string;
  country: string;
  deadline: string;     // ISO YYYY-MM-DD; "" if unknown
  url: string;
  summary: string;
  source: string;
  category: string;
  value?: string;
  published?: string;
}

interface LiveFeed {
  id: string; label: string; url: string; color: string; source: string;
}

// ── State ─────────────────────────────────────────────────────────────────────
let activeTab: NPTab = 'news';
let newsCache: { items: NewsArticle[]; ts: number } | null = null;
let eventsCache: { items: EventItem[]; ts: number } | null = null;
let jobsCache: { items: JobItem[]; ts: number } | null = null;
let opportunitiesCache: { items: OpportunityItem[]; ts: number } | null = null;
let opportunitySourceFilter: string = 'all';
let opportunityCountryFilter: string = 'all';
const CACHE_TTL = 60 * 60 * 1000; // 1 hour
let activeNewsFilters = new Set<string>();
let newsSearchQuery = '';

// ── Feed config ───────────────────────────────────────────────────────────────
const LIVE_FEEDS: LiveFeed[] = [
  { id: 'africandrone', label: 'africanDRONE', url: 'https://medium.com/feed/africandrone-organization',  color: '#10b981', source: 'africanDRONE' },
  { id: 'dronelife',    label: 'DroneLife',    url: 'https://dronelife.com/feed/',                        color: '#2563eb', source: 'DroneLife'   },
  { id: 'suas',         label: 'sUAS News',    url: 'https://www.suasnews.com/feed/',                     color: '#d97706', source: 'sUAS News'   },
  { id: 'uasweekly',   label: 'UAS Weekly',   url: 'https://uasweekly.com/feed/',                        color: '#7c3aed', source: 'UAS Weekly'  },
  { id: 'r_drones',    label: 'Reddit',        url: 'https://www.reddit.com/r/drones/.rss',               color: '#ff4500', source: 'r/drones'    },
  { id: 'r_ardupilot', label: 'Reddit',        url: 'https://www.reddit.com/r/ardupilot/.rss',            color: '#ff4500', source: 'r/ardupilot' },
  { id: 'r_multi',     label: 'Reddit',        url: 'https://www.reddit.com/r/Multicopter/.rss',          color: '#ff4500', source: 'r/Multicopter'},
];

const NEWS_SOURCES = [
  { id: 'africandrone', label: 'africanDRONE', color: '#10b981' },
  { id: 'dronelife',    label: 'DroneLife',    color: '#2563eb' },
  { id: 'suas',         label: 'sUAS News',    color: '#d97706' },
  { id: 'uasweekly',   label: 'UAS Weekly',   color: '#7c3aed' },
  { id: 'reddit',       label: '🤖 Reddit',    color: '#ff4500' },
];

function toChipId(source: string): string {
  if (source.startsWith('r/') || source.toLowerCase().includes('reddit')) return 'reddit';
  if (source === 'DroneLife')    return 'dronelife';
  if (source === 'sUAS News')    return 'suas';
  if (source === 'UAS Weekly')   return 'uasweekly';
  if (source === 'africanDRONE' || source.toLowerCase().includes('africandrone')) return 'africandrone';
  return 'other';
}

const EVENTS_TYPE_ICONS: Record<string, string> = {
  conference: '🏛️', workshop: '🔧', competition: '🏆',
  expo: '🏟️', training: '📚', festival: '🎉', event: '📅',
};

/* ── Lifecycle ────────────────────────────────────────────────────────────── */

export function initNewsPanel(): void {
  const overlay = document.getElementById('news-overlay')!;
  overlay.addEventListener('click', () => closeNewsPanel());
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const panel = document.getElementById('news-panel');
      if (panel?.classList.contains('open')) closeNewsPanel();
    }
  });
}

// Panel mode controls which set of tabs is shown:
//   'news'          → only the News tab
//   'opportunities' → Events / Jobs / Tenders tabs (no News tab)
type PanelMode = 'news' | 'opportunities';
let panelMode: PanelMode = 'news';

export function openNewsPanel(tab: NPTab = 'news'): void {
  closeSidebar();
  closeResourcesPanel();
  // Pick mode based on requested tab
  panelMode = (tab === 'news') ? 'news' : 'opportunities';
  activeTab = tab;
  renderPanel();
  document.getElementById('news-panel')!.classList.add('open');
  document.getElementById('news-overlay')!.classList.add('visible');
}

/** Open in Opportunities mode (Events / Jobs / Tenders).
 *  Defaults to the Tenders tab unless overridden. */
export function openOpportunitiesPanel(tab: NPTab = 'opportunities'): void {
  closeSidebar();
  closeResourcesPanel();
  panelMode = 'opportunities';
  activeTab = (tab === 'news') ? 'opportunities' : tab;
  renderPanel();
  document.getElementById('news-panel')!.classList.add('open');
  document.getElementById('news-overlay')!.classList.add('visible');
}

export function closeNewsPanel(): void {
  document.getElementById('news-panel')!.classList.remove('open');
  document.getElementById('news-overlay')!.classList.remove('visible');
}

export function isNewsPanelOpen(): boolean {
  return document.getElementById('news-panel')?.classList.contains('open') ?? false;
}

/* ── Render ──────────────────────────────────────────────────────────────── */

function renderPanel(): void {
  const panel = document.getElementById('news-panel')!;

  // Header label/icon depends on mode
  const headerIcon = panelMode === 'news' ? '📰' : '📋';
  const headerLabel = panelMode === 'news' ? 'News' : 'Opportunities';

  // Tabs to render — depends on mode
  const tabsForMode: Array<{id: NPTab; emoji: string; label: string}> =
    panelMode === 'news'
      ? [{ id: 'news', emoji: '📰', label: 'News' }]
      : [
          { id: 'events',        emoji: '📅', label: 'Events' },
          { id: 'jobs',          emoji: '💼', label: 'Jobs' },
          { id: 'opportunities', emoji: '📋', label: 'Tenders' },  // renamed from "Opportunities"
        ];

  // Body panels — render all internal tabs but only the active one is visible
  const bodyPanels = panelMode === 'news'
    ? `<div class="rp-panel${activeTab === 'news' ? ' active' : ''}" data-np-panel="news">${renderNewsTab()}</div>`
    : `
      <div class="rp-panel${activeTab === 'events' ? ' active' : ''}" data-np-panel="events">
        ${renderEventsTab()}
      </div>
      <div class="rp-panel${activeTab === 'jobs' ? ' active' : ''}" data-np-panel="jobs">
        ${renderJobsTab()}
      </div>
      <div class="rp-panel${activeTab === 'opportunities' ? ' active' : ''}" data-np-panel="opportunities">
        ${renderOpportunitiesTab()}
      </div>
    `;

  panel.innerHTML = `
    <div class="rp-header">
      <div class="rp-title-area">
        <span class="rp-title-icon">${headerIcon}</span>
        <h2 class="rp-title">${headerLabel}</h2>
      </div>
      <button class="rp-close" aria-label="Close panel">&times;</button>
    </div>
    <nav class="rp-tabs np-tabs" role="tablist">
      ${tabsForMode.map(t => `
        <button class="rp-tab${activeTab === t.id ? ' active' : ''}" data-np-tab="${t.id}" role="tab" aria-selected="${activeTab === t.id ? 'true' : 'false'}">
          <span class="rp-tab-emoji">${t.emoji}</span>
          <span class="rp-tab-label">${t.label}</span>
        </button>
      `).join('')}
    </nav>
    <div class="rp-body">
      ${bodyPanels}
    </div>
    <div class="rp-join-cta">
      <p class="pilot-cta-text">Get the <strong>weekly digest</strong> in your inbox — free.</p>
      <a href="/newsletter" target="_blank" rel="noopener" class="pilot-cta-btn">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="15" height="15"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
        Subscribe to africanDRONE Intelligence
      </a>
    </div>
  `;

  wirePanel(panel);
}

function renderNewsTab(): string {
  return `
    <div class="rp-tab-toolbar">
      <input class="rp-search rp-news-search" type="text" placeholder="Search articles…" id="np-news-search" />
    </div>
    <div class="rp-news-filters">
      <button class="rp-news-chip active" data-np-feed="all">All</button>
      ${NEWS_SOURCES.map(s => `
        <button class="rp-news-chip" data-np-feed="${s.id}" style="--chip-color:${s.color}">${s.label}</button>
      `).join('')}
    </div>
    <div class="rp-news-list" id="np-news-list">
      <div class="rp-news-loading">
        ${'<div class="rp-news-skeleton"></div>'.repeat(4)}
      </div>
    </div>
  `;
}

function renderEventsTab(): string {
  return `
    <div class="rp-events-list" id="np-events-list">
      <div class="rp-news-loading">
        ${'<div class="rp-news-skeleton"></div>'.repeat(3)}
      </div>
    </div>
  `;
}

function renderJobsTab(): string {
  return `
    <div class="rp-jobs-list" id="np-jobs-list">
      <div class="rp-news-loading">
        ${'<div class="rp-news-skeleton"></div>'.repeat(4)}
      </div>
    </div>
  `;
}

function renderOpportunitiesTab(): string {
  return `
    <div class="rp-opp-toolbar">
      <input class="rp-search rp-opp-search" type="text" placeholder="Search RFPs, tenders, mapping contracts…" id="np-opp-search" />
    </div>
    <div class="rp-opp-filters" id="np-opp-filters">
      <!-- filter chips injected after data loads -->
    </div>
    <div class="rp-opp-list" id="np-opp-list">
      <div class="rp-news-loading">
        ${'<div class="rp-news-skeleton"></div>'.repeat(4)}
      </div>
    </div>
  `;
}

/* ── Wire Events ─────────────────────────────────────────────────────────── */

function wirePanel(panel: HTMLElement): void {
  panel.querySelector('.rp-close')!.addEventListener('click', () => closeNewsPanel());

  // Tab switching
  panel.querySelectorAll('[data-np-tab]').forEach(btn => {
    btn.addEventListener('click', () => {
      activeTab = (btn as HTMLElement).dataset.npTab as NPTab;
      panel.querySelectorAll('[data-np-tab]').forEach(t => {
        const isActive = (t as HTMLElement).dataset.npTab === activeTab;
        t.classList.toggle('active', isActive);
        t.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      panel.querySelectorAll('[data-np-panel]').forEach(p =>
        p.classList.toggle('active', (p as HTMLElement).dataset.npPanel === activeTab));
      if (activeTab === 'news')   fetchAndRenderNews();
      if (activeTab === 'events') fetchAndRenderEvents();
      if (activeTab === 'jobs')   fetchAndRenderJobs();
      if (activeTab === 'opportunities') fetchAndRenderOpportunities();
    });
  });

  // News filter chips
  panel.querySelectorAll('.rp-news-chip[data-np-feed]').forEach(chip => {
    chip.addEventListener('click', () => {
      const feedId = (chip as HTMLElement).dataset.npFeed!;
      if (feedId === 'all') {
        activeNewsFilters.clear();
        panel.querySelectorAll('.rp-news-chip[data-np-feed]').forEach(c => c.classList.remove('active'));
        chip.classList.add('active');
      } else {
        panel.querySelector('.rp-news-chip[data-np-feed="all"]')?.classList.remove('active');
        chip.classList.toggle('active');
        if (chip.classList.contains('active')) activeNewsFilters.add(feedId);
        else activeNewsFilters.delete(feedId);
        if (activeNewsFilters.size === 0)
          panel.querySelector('.rp-news-chip[data-np-feed="all"]')?.classList.add('active');
      }
      if (newsCache) renderNewsCards(newsCache.items);
    });
  });

  // News search
  panel.querySelector('#np-news-search')?.addEventListener('input', (e) => {
    newsSearchQuery = (e.target as HTMLInputElement).value.toLowerCase().trim();
    if (newsCache) renderNewsCards(newsCache.items);
  });

  // Load whichever tab is active
  if (activeTab === 'news')   fetchAndRenderNews();
  if (activeTab === 'events') fetchAndRenderEvents();
  if (activeTab === 'jobs')   fetchAndRenderJobs();
  if (activeTab === 'opportunities') fetchAndRenderOpportunities();
}

/* ── News Fetching ──────────────────────────────────────────────────────── */

const FEED_TIMEOUT_MS = 10_000;

async function fetchAndRenderNews(): Promise<void> {
  if (newsCache && Date.now() - newsCache.ts < CACHE_TTL) {
    renderNewsCards(newsCache.items);
    return;
  }

  const listEl = document.getElementById('np-news-list');
  if (!listEl) return;
  listEl.innerHTML = `<div class="rp-news-loading">${'<div class="rp-news-skeleton"></div>'.repeat(5)}</div>`;

  // 1. Curated digest
  const curated: NewsArticle[] = [];
  try {
    const res = await fetch('/data/drone_news.json', { cache: 'no-cache' });
    if (res.ok) {
      const data = await res.json();
      for (const f of (data.features || [])) {
        const p = f.properties;
        const chipId = toChipId(p.source || '');
        curated.push({
          id:          p.id || '',
          title:       p.title || '',
          url:         p.url || '',
          excerpt:     (p.summary || '').slice(0, 140),
          source:      p.source || '',
          sourceLabel: chipId === 'reddit' ? 'Reddit' : (p.source || ''),
          sourceColor: NEWS_SOURCES.find(s => s.id === chipId)?.color ?? '#64748b',
          pub_date:    p.pub_date || '',
          chipId,
        });
      }
    }
  } catch { /* fall through to live feeds */ }

  // 2. Live RSS feeds
  const liveAll: NewsArticle[] = [];
  await Promise.allSettled(LIVE_FEEDS.map(async (feed) => {
    try { liveAll.push(...await fetchOneFeed(feed)); } catch { /* skip */ }
  }));

  const liveSafe = liveAll.filter(a => !isNewsDisqualified(a.title, a.excerpt));
  const seenSourceDay = new Set<string>();
  const liveCapped: NewsArticle[] = [];
  for (const a of liveSafe) {
    const key = `${a.source}::${(a.pub_date || '').slice(0, 10)}`;
    if (!seenSourceDay.has(key)) { seenSourceDay.add(key); liveCapped.push(a); }
  }

  // 3. Merge — curated articles (from the weekly newsletter) ALWAYS pin to
  // the top in their original score order, then today's RSS feeds sort by
  // date underneath. Previously the merge sorted everything by pub_date,
  // which buried our curated content under Reddit posts dated 'Today'.
  // Per the funder audit (June 2026): the News tab must surface the same
  // articles the subscribers got in this week's newsletter.
  const seenTitles = new Set<string>();
  const topCurated: NewsArticle[] = [];
  for (const a of curated) {
    const norm = a.title.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 40);
    if (!seenTitles.has(norm)) { seenTitles.add(norm); topCurated.push(a); }
  }
  const liveDeduped: NewsArticle[] = [];
  for (const a of liveCapped) {
    const norm = a.title.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 40);
    if (!seenTitles.has(norm)) { seenTitles.add(norm); liveDeduped.push(a); }
  }
  liveDeduped.sort((a, b) => (b.pub_date > a.pub_date ? 1 : -1));
  const merged: NewsArticle[] = [...topCurated, ...liveDeduped];

  newsCache = { items: merged, ts: Date.now() };
  renderNewsCards(merged);
}

async function fetchOneFeed(feed: LiveFeed): Promise<NewsArticle[]> {
  const chipId = toChipId(feed.source);
  const color  = NEWS_SOURCES.find(s => s.id === chipId)?.color ?? '#64748b';

  const toArticles = (items: { title: string; link: string; pubDate: string; description: string }[]): NewsArticle[] =>
    items.slice(0, 5).map(item => ({
      id:          btoa(item.link).slice(0, 12),
      title:       item.title || '',
      url:         item.link  || '',
      excerpt:     stripHtml(item.description || '').slice(0, 140),
      source:      feed.source,
      sourceLabel: feed.label,
      sourceColor: color,
      pub_date:    item.pubDate || '',
      chipId,
    }));

  // rss2json
  try {
    const r = await fetch(`https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(feed.url)}`, { signal: AbortSignal.timeout(FEED_TIMEOUT_MS) });
    if (r.ok) {
      const d = await r.json();
      if (d.status === 'ok' && d.items?.length) return toArticles(d.items);
    }
  } catch { /* fall through */ }

  // corsproxy fallback
  try {
    const r2 = await fetch(`https://corsproxy.io/?${encodeURIComponent(feed.url)}`, { signal: AbortSignal.timeout(FEED_TIMEOUT_MS) });
    if (r2.ok) {
      const xml  = await r2.text();
      const doc  = new DOMParser().parseFromString(xml, 'text/xml');
      const xmlItems = [...doc.querySelectorAll('item')].slice(0, 5).map(el => ({
        title:       el.querySelector('title')?.textContent || '',
        link:        el.querySelector('link')?.textContent  || '',
        pubDate:     el.querySelector('pubDate')?.textContent || '',
        description: el.querySelector('description')?.textContent || '',
      }));
      if (xmlItems.length) return toArticles(xmlItems);
    }
  } catch { /* give up */ }

  return [];
}

function renderNewsCards(items: NewsArticle[]): void {
  const listEl = document.getElementById('np-news-list');
  if (!listEl) return;

  let visible = activeNewsFilters.size === 0
    ? items
    : items.filter(a => activeNewsFilters.has(a.chipId));

  if (newsSearchQuery) {
    const q = newsSearchQuery;
    visible = visible.filter(a =>
      a.title.toLowerCase().includes(q) ||
      a.excerpt.toLowerCase().includes(q) ||
      a.source.toLowerCase().includes(q));
  }

  if (visible.length === 0) {
    listEl.innerHTML = '<div class="rp-news-empty">No articles found.</div>';
    return;
  }

  listEl.innerHTML = visible.map(item => `
    <a class="rp-news-card" href="${item.url}" target="_blank" rel="noopener" data-feed="${item.chipId}">
      <span class="rp-news-source" style="--source-color:${item.sourceColor}">${item.sourceLabel}</span>
      <span class="rp-news-title">${item.title}</span>
      ${item.excerpt ? `<span class="rp-news-excerpt">${item.excerpt}</span>` : ''}
      <span class="rp-news-date">${item.source} · ${formatDate(item.pub_date)}</span>
    </a>
  `).join('');
}

/* ── Events Fetching ─────────────────────────────────────────────────────── */

// African country/city tokens for the Africa-only filter on Events + Jobs.
// Matches the same set the newsletter uses (jobs_scraper.is_africa_located).
const AFRICA_TOKENS = [
  'africa','african','sub-saharan',
  'algeria','angola','benin','botswana','burkina faso','burundi','cabo verde','cape verde',
  'cameroon','central african republic','chad','comoros','congo','drc','dr congo',
  "côte d'ivoire","cote d'ivoire","ivory coast",
  'djibouti','egypt','equatorial guinea','eritrea','eswatini','swaziland','ethiopia',
  'gabon','gambia','ghana','guinea','guinea-bissau','kenya','lesotho','liberia','libya',
  'madagascar','malawi','mali','mauritania','mauritius','morocco','mozambique','namibia',
  'niger','nigeria','rwanda','senegal','seychelles','sierra leone','somalia','south africa',
  'south sudan','sudan','tanzania','togo','tunisia','uganda','western sahara','zambia','zimbabwe',
  'são tomé','sao tome',
  // major cities (so location strings like "Nairobi" alone match)
  'lagos','abuja','nairobi','mombasa','accra','kumasi','kigali','kampala','addis ababa',
  'dar es salaam','khartoum','cairo','cape town','johannesburg','durban','pretoria',
  'casablanca','rabat','tunis','algiers','lusaka','harare','windhoek','gaborone','maputo',
  'luanda','abidjan','dakar','ouagadougou','bamako','monrovia','freetown','antananarivo',
  'lilongwe','mogadishu','kinshasa','brazzaville','yaoundé','douala','asmara','bissau','praia',
];

function isAfricaLocated(loc: string, title = '', desc = ''): boolean {
  const blob = `${loc || ''} ${title || ''} ${desc || ''}`.toLowerCase();
  return AFRICA_TOKENS.some(t => blob.includes(t));
}

async function fetchAndRenderEvents(): Promise<void> {
  if (eventsCache && Date.now() - eventsCache.ts < CACHE_TTL) {
    renderEventCards(eventsCache.items);
    return;
  }

  const listEl = document.getElementById('np-events-list');
  if (!listEl) return;
  listEl.innerHTML = `<div class="rp-news-loading">${'<div class="rp-news-skeleton"></div>'.repeat(3)}</div>`;

  try {
    const res = await fetch('/data/events.json', { cache: 'no-cache' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const today = new Date().toISOString().slice(0, 10);
    // Africa-only filter (matches the newsletter's pick_events Africa-floor).
    // Per the funder audit (June 2026): the Events tab on the map must
    // not surface Texas/Vegas/Arlington events to African subscribers.
    const items: EventItem[] = (data.events || [])
      .filter((e: EventItem) => e.date >= today)
      .filter((e: EventItem) => isAfricaLocated(e.location || '', e.title || '', e.description || ''));
    eventsCache = { items, ts: Date.now() };
    renderEventCards(items);
  } catch {
    const listEl2 = document.getElementById('np-events-list');
    if (listEl2) listEl2.innerHTML = '<div class="rp-events-empty">No upcoming events found.</div>';
  }
}

function renderEventCards(items: EventItem[]): void {
  const listEl = document.getElementById('np-events-list');
  if (!listEl) return;

  if (items.length === 0) {
    listEl.innerHTML = '<div class="rp-events-empty">No upcoming events found.</div>';
    return;
  }

  listEl.innerHTML = items.map(e => {
    const icon = EVENTS_TYPE_ICONS[e.type] || '📅';
    const dateFormatted = (() => {
      try {
        return new Date(e.date + 'T00:00:00').toLocaleDateString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric'
        });
      } catch { return e.date; }
    })();
    return `
      <a class="rp-event-card" href="${e.url}" target="_blank" rel="noopener">
        <div class="rp-event-meta">
          <span class="rp-event-type">${icon} ${e.type}</span>
          <span class="rp-event-date">📅 ${dateFormatted}</span>
          ${e.location ? `<span class="rp-event-location">📍 ${e.location}</span>` : ''}
        </div>
        <span class="rp-event-title">${e.title}</span>
        ${e.description ? `<span class="rp-event-desc">${e.description}</span>` : ''}
      </a>
    `;
  }).join('');
}

/* ── Jobs Fetching ───────────────────────────────────────────────────────── */

async function fetchAndRenderJobs(): Promise<void> {
  if (jobsCache && Date.now() - jobsCache.ts < CACHE_TTL) {
    renderJobCards(jobsCache.items);
    return;
  }

  const listEl = document.getElementById('np-jobs-list');
  if (!listEl) return;
  listEl.innerHTML = `<div class="rp-news-loading">${'<div class="rp-news-skeleton"></div>'.repeat(4)}</div>`;

  try {
    const res = await fetch('/data/jobs.json', { cache: 'no-cache' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    // Africa-only filter — matches the newsletter's pick_jobs Africa-floor.
    // Per the funder audit (June 2026): the Jobs tab must not surface
    // US/UK/Saudi roles to subscribers who came here for African drone work.
    const items: JobItem[] = (data.jobs || []).filter((j: JobItem) =>
      isAfricaLocated(j.location || '', j.title || '', j.summary || ''));
    jobsCache = { items, ts: Date.now() };
    renderJobCards(items);
  } catch {
    const listEl2 = document.getElementById('np-jobs-list');
    if (listEl2) listEl2.innerHTML = '<div class="rp-events-empty">Could not load jobs. Try again later.</div>';
  }
}

function renderJobCards(items: JobItem[]): void {
  const listEl = document.getElementById('np-jobs-list');
  if (!listEl) return;

  if (items.length === 0) {
    listEl.innerHTML = `
      <div class="rp-events-empty">No jobs found.</div>
      <p style="text-align:center;margin-top:8px;font-size:12px;color:var(--color-text-muted)">
        Updated regularly.
      </p>
    `;
    return;
  }

  const pubDateStr = (pub_date: string) => {
    try {
      return new Date(pub_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch { return ''; }
  };

  // Freshness line derived from the most recent job pub_date in the loaded data.
  const mostRecent = items
    .map(j => j.pub_date)
    .filter(Boolean)
    .sort()
    .pop();
  const updatedLine = mostRecent
    ? `<p style="text-align:center;margin:0 0 10px;font-size:12px;color:var(--color-text-muted)">Listings updated ${(() => {
        try { return new Date(mostRecent).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
        catch { return mostRecent; }
      })()}</p>`
    : '';

  listEl.innerHTML = updatedLine + items.map(job => `
    <a class="rp-job-card" href="${job.url}" target="_blank" rel="noopener">
      <div class="rp-job-header">
        <span class="rp-job-title">${job.title}</span>
        ${job.company && job.company !== '—' ? `<span class="rp-job-company">${job.company}</span>` : ''}
      </div>
      <div class="rp-job-meta">
        ${job.location ? `<span class="rp-job-location">📍 ${job.location}</span>` : ''}
        <span class="rp-job-source">${job.source}${job.pub_date ? ' · ' + pubDateStr(job.pub_date) : ''}</span>
      </div>
      ${job.summary ? `<span class="rp-job-summary">${job.summary.replace(/&#160;/g, ' ').replace(/&amp;/g, '&')}</span>` : ''}
      <span class="rp-job-cta">View job →</span>
    </a>
  `).join('');

  // Append "More jobs" footer
  listEl.innerHTML += `
    <a class="rp-job-more" href="https://www.suasnews.com/category/the-market/jobs/" target="_blank" rel="noopener">
      More drone jobs → sUAS News Jobs Board
    </a>
  `;
}

/* ── Opportunities (RFPs / Tenders) Fetching ─────────────────────────────── */

async function fetchAndRenderOpportunities(): Promise<void> {
  if (opportunitiesCache && Date.now() - opportunitiesCache.ts < CACHE_TTL) {
    renderOpportunityCards(opportunitiesCache.items);
    return;
  }
  const listEl = document.getElementById('np-opp-list');
  if (!listEl) return;
  listEl.innerHTML = `<div class="rp-news-loading">${'<div class="rp-news-skeleton"></div>'.repeat(4)}</div>`;

  try {
    const res = await fetch('/data/opportunities.json', { cache: 'no-cache' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const today = new Date().toISOString().slice(0, 10);
    // Auto-expire: drop deadlines that have passed (if any deadline is set)
    const items: OpportunityItem[] = (data.opportunities || []).filter(
      (o: OpportunityItem) => !o.deadline || o.deadline >= today
    );
    opportunitiesCache = { items, ts: Date.now() };
    wireOpportunityFilters(items);
    renderOpportunityCards(items);
  } catch {
    listEl.innerHTML = `
      <div class="rp-events-empty">No active opportunities right now.</div>
      <p style="text-align:center;margin-top:8px;font-size:12px;color:var(--color-text-dim)">
        New tenders post daily. We scan UNGM, World Bank, AfDB, UNDP, and 16 national procurement portals.
      </p>
    `;
  }
}

function wireOpportunityFilters(items: OpportunityItem[]): void {
  const filtersEl = document.getElementById('np-opp-filters');
  if (!filtersEl) return;
  const sources = Array.from(new Set(items.map(i => i.source))).sort();
  const countries = Array.from(new Set(items.map(i => i.country).filter(Boolean))).sort();

  filtersEl.innerHTML = `
    <div class="rp-opp-filter-group">
      <button class="rp-news-chip${opportunitySourceFilter === 'all' ? ' active' : ''}" data-np-opp-source="all">All sources</button>
      ${sources.map(s => `<button class="rp-news-chip${opportunitySourceFilter === s ? ' active' : ''}" data-np-opp-source="${s}">${s}</button>`).join('')}
    </div>
    ${countries.length ? `
      <div class="rp-opp-filter-group">
        <button class="rp-news-chip${opportunityCountryFilter === 'all' ? ' active' : ''}" data-np-opp-country="all">All countries</button>
        ${countries.map(c => `<button class="rp-news-chip${opportunityCountryFilter === c ? ' active' : ''}" data-np-opp-country="${c}">${c}</button>`).join('')}
      </div>` : ''}
  `;

  filtersEl.querySelectorAll('[data-np-opp-source]').forEach(btn => {
    btn.addEventListener('click', () => {
      opportunitySourceFilter = (btn as HTMLElement).dataset.npOppSource!;
      filtersEl.querySelectorAll('[data-np-opp-source]').forEach(b => b.classList.toggle('active', b === btn));
      if (opportunitiesCache) renderOpportunityCards(opportunitiesCache.items);
    });
  });
  filtersEl.querySelectorAll('[data-np-opp-country]').forEach(btn => {
    btn.addEventListener('click', () => {
      opportunityCountryFilter = (btn as HTMLElement).dataset.npOppCountry!;
      filtersEl.querySelectorAll('[data-np-opp-country]').forEach(b => b.classList.toggle('active', b === btn));
      if (opportunitiesCache) renderOpportunityCards(opportunitiesCache.items);
    });
  });

  document.getElementById('np-opp-search')?.addEventListener('input', () => {
    if (opportunitiesCache) renderOpportunityCards(opportunitiesCache.items);
  });
}

function renderOpportunityCards(items: OpportunityItem[]): void {
  const listEl = document.getElementById('np-opp-list');
  if (!listEl) return;

  const q = (document.getElementById('np-opp-search') as HTMLInputElement | null)?.value.toLowerCase().trim() || '';
  let filtered = items;
  if (opportunitySourceFilter !== 'all')
    filtered = filtered.filter(i => i.source === opportunitySourceFilter);
  if (opportunityCountryFilter !== 'all')
    filtered = filtered.filter(i => i.country === opportunityCountryFilter);
  if (q)
    filtered = filtered.filter(i =>
      i.title.toLowerCase().includes(q) ||
      (i.summary || '').toLowerCase().includes(q) ||
      (i.country || '').toLowerCase().includes(q) ||
      (i.issuer || '').toLowerCase().includes(q));

  if (filtered.length === 0) {
    listEl.innerHTML = `
      <div class="rp-events-empty">No opportunities match your filters.</div>
    `;
    return;
  }

  const today = new Date();
  const fmtDeadline = (iso: string): { label: string; tone: 'soon' | 'ok' | 'unknown' } => {
    if (!iso) return { label: 'Open / no deadline listed', tone: 'unknown' };
    try {
      const d = new Date(iso + 'T00:00:00');
      const days = Math.round((d.getTime() - today.getTime()) / 86_400_000);
      const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const suffix = days <= 0 ? ' (today)' :
                     days === 1 ? ' (tomorrow)' :
                     days <= 30 ? ` (${days} days)` : '';
      // ≤7 days → yellow ("soon"); >7 → green ("ok")
      const tone: 'soon' | 'ok' = days <= 7 ? 'soon' : 'ok';
      return { label: label + suffix, tone };
    } catch { return { label: iso, tone: 'unknown' }; }
  };

  const renderCard = (o: OpportunityItem): string => {
    const dl = fmtDeadline(o.deadline);
    return `
      <a class="rp-opp-card tone-${dl.tone}" href="${o.url}" target="_blank" rel="noopener">
        <div class="rp-opp-header">
          <div class="rp-opp-pills">
            ${o.country ? `<span class="rp-opp-pill rp-opp-pill-country">📍 ${o.country}</span>` : ''}
            <span class="rp-opp-category">${o.category || 'Geospatial'}</span>
          </div>
          <span class="rp-opp-deadline tone-${dl.tone}">⏱ ${dl.label}</span>
        </div>
        <span class="rp-opp-title">${o.title}</span>
        <div class="rp-opp-meta">
          ${o.issuer ? `<span class="rp-opp-issuer">🏛 ${o.issuer}</span>` : ''}
          <span class="rp-opp-source">${o.source}</span>
          ${o.value ? `<span class="rp-opp-value">💰 ${o.value}</span>` : ''}
        </div>
        ${o.summary ? `<span class="rp-opp-summary">${o.summary}</span>` : ''}
        <span class="rp-opp-cta">View notice →</span>
      </a>
    `;
  };

  // Single chronological list — data is pre-filtered Africa-only on the server.
  // No-deadline items sink to the bottom.
  const sorted = [...filtered].sort((a, b) => {
    const ad = a.deadline || '';
    const bd = b.deadline || '';
    if (!ad && !bd) return 0;
    if (!ad) return 1;
    if (!bd) return -1;
    return ad.localeCompare(bd);
  });

  listEl.innerHTML = sorted.map(renderCard).join('') + `
    <p class="rp-opp-footer">
      Sourced daily from UNGM, World Bank, AfDB, TED Europa, ReliefWeb, AWF, SA eTenders, SAM.gov + more.
      Tip: contact us at <a href="mailto:support@worlddronemap.com">support@worlddronemap.com</a> if a critical portal is missing.
    </p>
  `;
}

// Africa membership check — matches the scraper's AFRICAN_COUNTRIES list
export const AFRICAN_COUNTRY_NAMES = new Set([
  'algeria','angola','benin','botswana','burkina faso','burundi',
  'cabo verde','cape verde','cameroon','central african republic','chad',
  'comoros','congo','drc','democratic republic of the congo',
  "côte d'ivoire","cote d'ivoire",'ivory coast','djibouti','egypt',
  'equatorial guinea','eritrea','eswatini','swaziland','ethiopia',
  'gabon','gambia','ghana','guinea','guinea-bissau','kenya',
  'lesotho','liberia','libya','madagascar','malawi','mali',
  'mauritania','mauritius','morocco','mozambique','namibia','niger',
  'nigeria','rwanda','são tomé','sao tome','senegal','seychelles',
  'sierra leone','somalia','south africa','south sudan','sudan',
  'tanzania','togo','tunisia','uganda','western sahara','zambia','zimbabwe',
]);
export function isAfricanCountry(country: string | undefined): boolean {
  if (!country) return false;
  return AFRICAN_COUNTRY_NAMES.has(country.trim().toLowerCase());
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function stripHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - d.getTime()) / 86_400_000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7)  return `${diffDays}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}
